import {
  TextStyle_TextAlignment,
  Link_Target,
  PluginContainerData_Alignment,
  PluginContainerData_Width_Type,
  Gradient_Type,
} from './common.enums.js';

/* eslint-disable */
export interface TextStyle {
  /** Text alignment. Defaults to `AUTO`. */
  textAlignment: TextStyle_TextAlignment;
  /** A CSS `line-height` value for the text expressed as a ratio relative to the font size. For example, if the font size is 20px, a `lineHeight` value of `'1.5'`` results in a line height of 30px. */
  lineHeight?: string;
}

export function textStyle_TextAlignmentFromJSON(
  object: any
): TextStyle_TextAlignment {
  switch (object) {
    case 0:
    case "AUTO":
      return TextStyle_TextAlignment.AUTO;
    case 1:
    case "LEFT":
      return TextStyle_TextAlignment.LEFT;
    case 2:
    case "RIGHT":
      return TextStyle_TextAlignment.RIGHT;
    case 3:
    case "CENTER":
      return TextStyle_TextAlignment.CENTER;
    case 4:
    case "JUSTIFY":
      return TextStyle_TextAlignment.JUSTIFY;
    case -1:
    case "UNRECOGNIZED":
    default:
      return TextStyle_TextAlignment.UNRECOGNIZED;
  }
}

export function textStyle_TextAlignmentToJSON(
  object: TextStyle_TextAlignment
): string {
  switch (object) {
    case TextStyle_TextAlignment.AUTO:
      return "AUTO";
    case TextStyle_TextAlignment.LEFT:
      return "LEFT";
    case TextStyle_TextAlignment.RIGHT:
      return "RIGHT";
    case TextStyle_TextAlignment.CENTER:
      return "CENTER";
    case TextStyle_TextAlignment.JUSTIFY:
      return "JUSTIFY";
    default:
      return "UNKNOWN";
  }
}

export interface Link {
  /** The absolute URL for the linked document. */
  url: string | undefined;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor: string | undefined;
  /**
   * The HTML `target` attribute value for the link. This property defines where the linked document opens as follows:
   * `SELF` - Default. Opens the linked document in the same frame as the link.
   * `BLANK` - Opens the linked document in a new browser tab or window.
   * `PARENT` - Opens the linked document in the link's parent frame.
   * `TOP` - Opens the linked document in the full body of the link's browser tab or window.
   */
  target: Link_Target;
  /** The HTML `rel` attribute value for the link. This object specifies the relationship between the current document and the linked document. */
  rel?: Link_Rel;
  /** A serialized object used for a custom or external link panel. */
  customData?: string;
}

export function link_TargetFromJSON(object: any): Link_Target {
  switch (object) {
    case 0:
    case "SELF":
      return Link_Target.SELF;
    case 1:
    case "BLANK":
      return Link_Target.BLANK;
    case 2:
    case "PARENT":
      return Link_Target.PARENT;
    case 3:
    case "TOP":
      return Link_Target.TOP;
    case -1:
    case "UNRECOGNIZED":
    default:
      return Link_Target.UNRECOGNIZED;
  }
}

export function link_TargetToJSON(object: Link_Target): string {
  switch (object) {
    case Link_Target.SELF:
      return "SELF";
    case Link_Target.BLANK:
      return "BLANK";
    case Link_Target.PARENT:
      return "PARENT";
    case Link_Target.TOP:
      return "TOP";
    default:
      return "UNKNOWN";
  }
}

export interface Link_Rel {
  /** Indicates to search engine crawlers not to follow the link. Defaults to `false`. */
  nofollow?: boolean;
  /** Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement. Defaults to `false`. */
  sponsored?: boolean;
  /** Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page’s author. For example, a link in a fourm post. Defaults to `false`. */
  ugc?: boolean;
  /** Indicates that this link protect referral information from being passed to the target website. */
  noreferrer?: boolean;
}

export interface PluginContainerData {
  /** The width of the node when it's displayed. */
  width?: PluginContainerData_Width;
  /** The node's alignment within its container. */
  alignment: PluginContainerData_Alignment;
  /** Spoiler cover settings for the node. */
  spoiler?: PluginContainerData_Spoiler;
  /** The height of the node when it's displayed. */
  height?: PluginContainerData_Height;
  /** Sets whether text should wrap around this node when it's displayed. If `textWrap` is `false`, the node takes up the width of its container. Defaults to `true` for all node types except 'DIVIVDER' where it defaults to `false`. */
  textWrap?: boolean;
}

export function pluginContainerData_AlignmentFromJSON(
  object: any
): PluginContainerData_Alignment {
  switch (object) {
    case 0:
    case "CENTER":
      return PluginContainerData_Alignment.CENTER;
    case 1:
    case "LEFT":
      return PluginContainerData_Alignment.LEFT;
    case 2:
    case "RIGHT":
      return PluginContainerData_Alignment.RIGHT;
    case -1:
    case "UNRECOGNIZED":
    default:
      return PluginContainerData_Alignment.UNRECOGNIZED;
  }
}

export function pluginContainerData_AlignmentToJSON(
  object: PluginContainerData_Alignment
): string {
  switch (object) {
    case PluginContainerData_Alignment.CENTER:
      return "CENTER";
    case PluginContainerData_Alignment.LEFT:
      return "LEFT";
    case PluginContainerData_Alignment.RIGHT:
      return "RIGHT";
    default:
      return "UNKNOWN";
  }
}

export interface PluginContainerData_Spoiler {
  /** Sets whether the spoiler cover is enabled for this node. Defaults to `false`. */
  enabled?: boolean;
  /** The description displayed on top of the spoiler cover. */
  description?: string;
  /** The text for the button used to remove the spoiler cover. */
  buttonText?: string;
}

export interface PluginContainerData_Width {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size: PluginContainerData_Width_Type | undefined;
  /** A custom width value in pixels. */
  custom?: string;
}

export function pluginContainerData_Width_TypeFromJSON(
  object: any
): PluginContainerData_Width_Type {
  switch (object) {
    case 0:
    case "CONTENT":
      return PluginContainerData_Width_Type.CONTENT;
    case 1:
    case "SMALL":
      return PluginContainerData_Width_Type.SMALL;
    case 2:
    case "ORIGINAL":
      return PluginContainerData_Width_Type.ORIGINAL;
    case 3:
    case "FULL_WIDTH":
      return PluginContainerData_Width_Type.FULL_WIDTH;
    case -1:
    case "UNRECOGNIZED":
    default:
      return PluginContainerData_Width_Type.UNRECOGNIZED;
  }
}

export function pluginContainerData_Width_TypeToJSON(
  object: PluginContainerData_Width_Type
): string {
  switch (object) {
    case PluginContainerData_Width_Type.CONTENT:
      return "CONTENT";
    case PluginContainerData_Width_Type.SMALL:
      return "SMALL";
    case PluginContainerData_Width_Type.ORIGINAL:
      return "ORIGINAL";
    case PluginContainerData_Width_Type.FULL_WIDTH:
      return "FULL_WIDTH";
    default:
      return "UNKNOWN";
  }
}

export interface PluginContainerData_Height {
  /** A custom height value in pixels. */
  custom?: string;
}

export interface FileSource {
  /** The absolute URL for the file's source. */
  url?: string;
  /**
   * Custom ID. Use `id` instead.
   *
   * @deprecated
   */
  custom?: string;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string;
  /** Indicates whether the file's source is private. Defaults to `false`. */
  private?: boolean;
}

export interface Media {
  /** The source for the media's data. */
  src?: FileSource;
  /** Media width in pixels. */
  width?: number;
  /** Media height in pixels. */
  height?: number;
  /** Media duration in seconds. Only relevant for audio and video files. */
  duration?: number;
}

export interface PlaybackOptions {
  /** Sets whether the media will automatically start playing. */
  autoPlay?: boolean;
  /** Sets whether media's will be looped. */
  playInLoop?: boolean;
  /** Sets whether media's controls will be shown. */
  showControls?: boolean;
}

export interface Oembed {
  /** The resource type. */
  type?: string;
  /** The width of the resource specified in the `url` property in pixels. */
  width?: number;
  /** The height of the resource specified in the `url` property in pixels. */
  height?: number;
  /** Resource title. */
  title?: string;
  /** The source URL for the resource. */
  url?: string;
  /** HTML for embedding a video player. The HTML should have no padding or margins. */
  html?: string;
  /** The name of the author or owner of the resource. */
  authorName?: string;
  /** The URL for the author or owner of the resource. */
  authorUrl?: string;
  /** The name of the resource provider. */
  providerName?: string;
  /** The URL for the resource provider. */
  providerUrl?: string;
  /** The URL for a thumbnail image for the resource. If this property is defined, `thumbnailWidth` and `thumbnailHeight` must also be defined. */
  thumbnailUrl?: string;
  /** The width of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailHeight` must also be defined. */
  thumbnailWidth?: string;
  /** The height of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailWidth`must also be defined. */
  thumbnailHeight?: string;
  /** The URL for an embedded viedo. */
  videoUrl?: string;
  /** The oEmbed version number.  This value must be `1.0`. */
  version?: string;
}

export interface Gradient {
  /** Gradient type. */
  type: Gradient_Type;
  /** Color stops for the gradient. */
  stops: Gradient_Stop[];
  /** Angle in degrees for linear gradient (0-360). */
  angle?: number;
  /** Horizontal center position for radial gradient (0-100). */
  centerX?: number;
  /** Vertical center position for radial gradient (0-100). */
  centerY?: number;
}

export function gradient_TypeFromJSON(object: any): Gradient_Type {
  switch (object) {
    case 0:
    case "LINEAR":
      return Gradient_Type.LINEAR;
    case 1:
    case "RADIAL":
      return Gradient_Type.RADIAL;
    case -1:
    case "UNRECOGNIZED":
    default:
      return Gradient_Type.UNRECOGNIZED;
  }
}

export function gradient_TypeToJSON(object: Gradient_Type): string {
  switch (object) {
    case Gradient_Type.LINEAR:
      return "LINEAR";
    case Gradient_Type.RADIAL:
      return "RADIAL";
    default:
      return "UNKNOWN";
  }
}

/** A single color stop in the gradient. */
export interface Gradient_Stop {
  /** Stop color as hex value. */
  color?: string;
  /** Stop position (0-1). */
  position?: number;
}

const baseTextStyle: object = { textAlignment: TextStyle_TextAlignment.AUTO };

export const TextStyle = {
  fromJSON(object: any): TextStyle {
    const message = { ...baseTextStyle } as TextStyle;
    message.textAlignment =
      object.textAlignment !== undefined && object.textAlignment !== null
        ? textStyle_TextAlignmentFromJSON(object.textAlignment)
        : TextStyle_TextAlignment.AUTO;
    message.lineHeight =
      object.lineHeight !== undefined && object.lineHeight !== null
        ? String(object.lineHeight)
        : undefined;
    return message;
  },

  toJSON(message: TextStyle): unknown {
    const obj: any = {};
    message.textAlignment !== undefined &&
      (obj.textAlignment = textStyle_TextAlignmentToJSON(
        message.textAlignment
      ));
    message.lineHeight !== undefined && (obj.lineHeight = message.lineHeight);
    return obj;
  },
};

const baseLink: object = { target: Link_Target.SELF };

export const Link = {
  fromJSON(object: any): Link {
    const message = { ...baseLink } as Link;
    message.url =
      object.url !== undefined && object.url !== null
        ? String(object.url)
        : undefined;
    message.anchor =
      object.anchor !== undefined && object.anchor !== null
        ? String(object.anchor)
        : undefined;
    message.target =
      object.target !== undefined && object.target !== null
        ? link_TargetFromJSON(object.target)
        : Link_Target.SELF;
    message.rel =
      object.rel !== undefined && object.rel !== null
        ? Link_Rel.fromJSON(object.rel)
        : undefined;
    message.customData =
      object.customData !== undefined && object.customData !== null
        ? String(object.customData)
        : undefined;
    return message;
  },

  toJSON(message: Link): unknown {
    const obj: any = {};
    message.url !== undefined && (obj.url = message.url);
    message.anchor !== undefined && (obj.anchor = message.anchor);
    message.target !== undefined &&
      (obj.target = link_TargetToJSON(message.target));
    message.rel !== undefined &&
      (obj.rel = message.rel ? Link_Rel.toJSON(message.rel) : undefined);
    message.customData !== undefined && (obj.customData = message.customData);
    return obj;
  },
};

const baseLink_Rel: object = {};

export const Link_Rel = {
  fromJSON(object: any): Link_Rel {
    const message = { ...baseLink_Rel } as Link_Rel;
    message.nofollow =
      object.nofollow !== undefined && object.nofollow !== null
        ? Boolean(object.nofollow)
        : undefined;
    message.sponsored =
      object.sponsored !== undefined && object.sponsored !== null
        ? Boolean(object.sponsored)
        : undefined;
    message.ugc =
      object.ugc !== undefined && object.ugc !== null
        ? Boolean(object.ugc)
        : undefined;
    message.noreferrer =
      object.noreferrer !== undefined && object.noreferrer !== null
        ? Boolean(object.noreferrer)
        : undefined;
    return message;
  },

  toJSON(message: Link_Rel): unknown {
    const obj: any = {};
    message.nofollow !== undefined && (obj.nofollow = message.nofollow);
    message.sponsored !== undefined && (obj.sponsored = message.sponsored);
    message.ugc !== undefined && (obj.ugc = message.ugc);
    message.noreferrer !== undefined && (obj.noreferrer = message.noreferrer);
    return obj;
  },
};

const basePluginContainerData: object = {
  alignment: PluginContainerData_Alignment.CENTER,
};

export const PluginContainerData = {
  fromJSON(object: any): PluginContainerData {
    const message = { ...basePluginContainerData } as PluginContainerData;
    message.width =
      object.width !== undefined && object.width !== null
        ? PluginContainerData_Width.fromJSON(object.width)
        : undefined;
    message.alignment =
      object.alignment !== undefined && object.alignment !== null
        ? pluginContainerData_AlignmentFromJSON(object.alignment)
        : PluginContainerData_Alignment.CENTER;
    message.spoiler =
      object.spoiler !== undefined && object.spoiler !== null
        ? PluginContainerData_Spoiler.fromJSON(object.spoiler)
        : undefined;
    message.height =
      object.height !== undefined && object.height !== null
        ? PluginContainerData_Height.fromJSON(object.height)
        : undefined;
    message.textWrap =
      object.textWrap !== undefined && object.textWrap !== null
        ? Boolean(object.textWrap)
        : undefined;
    return message;
  },

  toJSON(message: PluginContainerData): unknown {
    const obj: any = {};
    message.width !== undefined &&
      (obj.width = message.width
        ? PluginContainerData_Width.toJSON(message.width)
        : undefined);
    message.alignment !== undefined &&
      (obj.alignment = pluginContainerData_AlignmentToJSON(message.alignment));
    message.spoiler !== undefined &&
      (obj.spoiler = message.spoiler
        ? PluginContainerData_Spoiler.toJSON(message.spoiler)
        : undefined);
    message.height !== undefined &&
      (obj.height = message.height
        ? PluginContainerData_Height.toJSON(message.height)
        : undefined);
    message.textWrap !== undefined && (obj.textWrap = message.textWrap);
    return obj;
  },
};

const basePluginContainerData_Spoiler: object = {};

export const PluginContainerData_Spoiler = {
  fromJSON(object: any): PluginContainerData_Spoiler {
    const message = {
      ...basePluginContainerData_Spoiler,
    } as PluginContainerData_Spoiler;
    message.enabled =
      object.enabled !== undefined && object.enabled !== null
        ? Boolean(object.enabled)
        : undefined;
    message.description =
      object.description !== undefined && object.description !== null
        ? String(object.description)
        : undefined;
    message.buttonText =
      object.buttonText !== undefined && object.buttonText !== null
        ? String(object.buttonText)
        : undefined;
    return message;
  },

  toJSON(message: PluginContainerData_Spoiler): unknown {
    const obj: any = {};
    message.enabled !== undefined && (obj.enabled = message.enabled);
    message.description !== undefined &&
      (obj.description = message.description);
    message.buttonText !== undefined && (obj.buttonText = message.buttonText);
    return obj;
  },
};

const basePluginContainerData_Width: object = {};

export const PluginContainerData_Width = {
  fromJSON(object: any): PluginContainerData_Width {
    const message = {
      ...basePluginContainerData_Width,
    } as PluginContainerData_Width;
    message.size =
      object.size !== undefined && object.size !== null
        ? pluginContainerData_Width_TypeFromJSON(object.size)
        : undefined;
    message.custom =
      object.custom !== undefined && object.custom !== null
        ? String(object.custom)
        : undefined;
    return message;
  },

  toJSON(message: PluginContainerData_Width): unknown {
    const obj: any = {};
    message.size !== undefined &&
      (obj.size =
        message.size !== undefined
          ? pluginContainerData_Width_TypeToJSON(message.size)
          : undefined);
    message.custom !== undefined && (obj.custom = message.custom);
    return obj;
  },
};

const basePluginContainerData_Height: object = {};

export const PluginContainerData_Height = {
  fromJSON(object: any): PluginContainerData_Height {
    const message = {
      ...basePluginContainerData_Height,
    } as PluginContainerData_Height;
    message.custom =
      object.custom !== undefined && object.custom !== null
        ? String(object.custom)
        : undefined;
    return message;
  },

  toJSON(message: PluginContainerData_Height): unknown {
    const obj: any = {};
    message.custom !== undefined && (obj.custom = message.custom);
    return obj;
  },
};

const baseFileSource: object = {};

export const FileSource = {
  fromJSON(object: any): FileSource {
    const message = { ...baseFileSource } as FileSource;
    message.url =
      object.url !== undefined && object.url !== null
        ? String(object.url)
        : undefined;
    message.custom =
      object.custom !== undefined && object.custom !== null
        ? String(object.custom)
        : undefined;
    message.id =
      object.id !== undefined && object.id !== null
        ? String(object.id)
        : undefined;
    message.private =
      object.private !== undefined && object.private !== null
        ? Boolean(object.private)
        : undefined;
    return message;
  },

  toJSON(message: FileSource): unknown {
    const obj: any = {};
    message.url !== undefined && (obj.url = message.url);
    message.custom !== undefined && (obj.custom = message.custom);
    message.id !== undefined && (obj.id = message.id);
    message.private !== undefined && (obj.private = message.private);
    return obj;
  },
};

const baseMedia: object = {};

export const Media = {
  fromJSON(object: any): Media {
    const message = { ...baseMedia } as Media;
    message.src =
      object.src !== undefined && object.src !== null
        ? FileSource.fromJSON(object.src)
        : undefined;
    message.width =
      object.width !== undefined && object.width !== null
        ? Number(object.width)
        : undefined;
    message.height =
      object.height !== undefined && object.height !== null
        ? Number(object.height)
        : undefined;
    message.duration =
      object.duration !== undefined && object.duration !== null
        ? Number(object.duration)
        : undefined;
    return message;
  },

  toJSON(message: Media): unknown {
    const obj: any = {};
    message.src !== undefined &&
      (obj.src = message.src ? FileSource.toJSON(message.src) : undefined);
    message.width !== undefined && (obj.width = message.width);
    message.height !== undefined && (obj.height = message.height);
    message.duration !== undefined && (obj.duration = message.duration);
    return obj;
  },
};

const basePlaybackOptions: object = {};

export const PlaybackOptions = {
  fromJSON(object: any): PlaybackOptions {
    const message = { ...basePlaybackOptions } as PlaybackOptions;
    message.autoPlay =
      object.autoPlay !== undefined && object.autoPlay !== null
        ? Boolean(object.autoPlay)
        : undefined;
    message.playInLoop =
      object.playInLoop !== undefined && object.playInLoop !== null
        ? Boolean(object.playInLoop)
        : undefined;
    message.showControls =
      object.showControls !== undefined && object.showControls !== null
        ? Boolean(object.showControls)
        : undefined;
    return message;
  },

  toJSON(message: PlaybackOptions): unknown {
    const obj: any = {};
    message.autoPlay !== undefined && (obj.autoPlay = message.autoPlay);
    message.playInLoop !== undefined && (obj.playInLoop = message.playInLoop);
    message.showControls !== undefined &&
      (obj.showControls = message.showControls);
    return obj;
  },
};

const baseOembed: object = {};

export const Oembed = {
  fromJSON(object: any): Oembed {
    const message = { ...baseOembed } as Oembed;
    message.type =
      object.type !== undefined && object.type !== null
        ? String(object.type)
        : undefined;
    message.width =
      object.width !== undefined && object.width !== null
        ? Number(object.width)
        : undefined;
    message.height =
      object.height !== undefined && object.height !== null
        ? Number(object.height)
        : undefined;
    message.title =
      object.title !== undefined && object.title !== null
        ? String(object.title)
        : undefined;
    message.url =
      object.url !== undefined && object.url !== null
        ? String(object.url)
        : undefined;
    message.html =
      object.html !== undefined && object.html !== null
        ? String(object.html)
        : undefined;
    message.authorName =
      object.authorName !== undefined && object.authorName !== null
        ? String(object.authorName)
        : undefined;
    message.authorUrl =
      object.authorUrl !== undefined && object.authorUrl !== null
        ? String(object.authorUrl)
        : undefined;
    message.providerName =
      object.providerName !== undefined && object.providerName !== null
        ? String(object.providerName)
        : undefined;
    message.providerUrl =
      object.providerUrl !== undefined && object.providerUrl !== null
        ? String(object.providerUrl)
        : undefined;
    message.thumbnailUrl =
      object.thumbnailUrl !== undefined && object.thumbnailUrl !== null
        ? String(object.thumbnailUrl)
        : undefined;
    message.thumbnailWidth =
      object.thumbnailWidth !== undefined && object.thumbnailWidth !== null
        ? String(object.thumbnailWidth)
        : undefined;
    message.thumbnailHeight =
      object.thumbnailHeight !== undefined && object.thumbnailHeight !== null
        ? String(object.thumbnailHeight)
        : undefined;
    message.videoUrl =
      object.videoUrl !== undefined && object.videoUrl !== null
        ? String(object.videoUrl)
        : undefined;
    message.version =
      object.version !== undefined && object.version !== null
        ? String(object.version)
        : undefined;
    return message;
  },

  toJSON(message: Oembed): unknown {
    const obj: any = {};
    message.type !== undefined && (obj.type = message.type);
    message.width !== undefined && (obj.width = message.width);
    message.height !== undefined && (obj.height = message.height);
    message.title !== undefined && (obj.title = message.title);
    message.url !== undefined && (obj.url = message.url);
    message.html !== undefined && (obj.html = message.html);
    message.authorName !== undefined && (obj.authorName = message.authorName);
    message.authorUrl !== undefined && (obj.authorUrl = message.authorUrl);
    message.providerName !== undefined &&
      (obj.providerName = message.providerName);
    message.providerUrl !== undefined &&
      (obj.providerUrl = message.providerUrl);
    message.thumbnailUrl !== undefined &&
      (obj.thumbnailUrl = message.thumbnailUrl);
    message.thumbnailWidth !== undefined &&
      (obj.thumbnailWidth = message.thumbnailWidth);
    message.thumbnailHeight !== undefined &&
      (obj.thumbnailHeight = message.thumbnailHeight);
    message.videoUrl !== undefined && (obj.videoUrl = message.videoUrl);
    message.version !== undefined && (obj.version = message.version);
    return obj;
  },
};

const baseGradient: object = { type: Gradient_Type.LINEAR };

export const Gradient = {
  fromJSON(object: any): Gradient {
    const message = { ...baseGradient } as Gradient;
    message.type =
      object.type !== undefined && object.type !== null
        ? gradient_TypeFromJSON(object.type)
        : Gradient_Type.LINEAR;
    message.stops = (object.stops ?? []).map((e: any) =>
      Gradient_Stop.fromJSON(e)
    );
    message.angle =
      object.angle !== undefined && object.angle !== null
        ? Number(object.angle)
        : undefined;
    message.centerX =
      object.centerX !== undefined && object.centerX !== null
        ? Number(object.centerX)
        : undefined;
    message.centerY =
      object.centerY !== undefined && object.centerY !== null
        ? Number(object.centerY)
        : undefined;
    return message;
  },

  toJSON(message: Gradient): unknown {
    const obj: any = {};
    message.type !== undefined &&
      (obj.type = gradient_TypeToJSON(message.type));
    if (message.stops) {
      obj.stops = message.stops.map((e) =>
        e ? Gradient_Stop.toJSON(e) : undefined
      );
    } else {
      obj.stops = [];
    }
    message.angle !== undefined && (obj.angle = message.angle);
    message.centerX !== undefined && (obj.centerX = message.centerX);
    message.centerY !== undefined && (obj.centerY = message.centerY);
    return obj;
  },
};

const baseGradient_Stop: object = {};

export const Gradient_Stop = {
  fromJSON(object: any): Gradient_Stop {
    const message = { ...baseGradient_Stop } as Gradient_Stop;
    message.color =
      object.color !== undefined && object.color !== null
        ? String(object.color)
        : undefined;
    message.position =
      object.position !== undefined && object.position !== null
        ? Number(object.position)
        : undefined;
    return message;
  },

  toJSON(message: Gradient_Stop): unknown {
    const obj: any = {};
    message.color !== undefined && (obj.color = message.color);
    message.position !== undefined && (obj.position = message.position);
    return obj;
  },
};
